
import "../../../libs/tonejs/Midi.js";
import "../../../libs/tonejs/tone-rhythm.min.js";
import "../../../libs/tonejs/Tone.js";

import { MelodyContext } from "../control/MelodyContext.js";
import { MelodyControl, MelodyControlStates } from "../control/MelodyControl.js";
import { Confetti } from "../display/Confetti.js";
import { Header } from "../display/Header.js";
import { Help } from "../display/Help.js";
import { MelodyInstrument } from "../display/MelodyInstrument.js";
import { MelodyInstrumentSelector } from "../display/MelodyInstrumentSelector.js";
import { MelodyNavigation } from "../display/MelodyNavigation.js";
import { MelodySelector } from "../display/MelodySelector.js";
import { Stage } from "../display/Stage.js";
import { Data } from "../model/Data.js";
import { VOMelodyInstrument } from "../model/DataStructure.js";
import { Idle } from "../model/Idle.js";
import { ProgressList } from "../model/ProgressList.js";

export interface MelodyAppOptions {
    element: HTMLDivElement;
    debug: boolean;
}

export class MelodyApp {

    private _element: HTMLDivElement;
    private _stage: Stage;
    private _data: Data;
    private _header: Header;
    private _instrumentSelector: MelodyInstrumentSelector;
    private _audioContext: AudioContext;
    private _instrument: MelodyInstrument;
    private _navigation: MelodyNavigation;
    private _help: Help;
    private _confetti: Confetti;
    private _control: MelodyControl;
    private _melodySelector: MelodySelector;

    constructor(private _options: MelodyAppOptions) {

        this._element = _options.element;

        this.init();
    }

    private async init() {
        try {
            const isRuntime = Boolean(window.api);
            const assetsUrl = this._element.getAttribute("data-base") + "/assets";

            this._stage = new Stage({
                element: this._element,
                className: "orchestra-app",
                elementOffsetHeight: isRuntime ? 0 : 200
            });

            new Promise((resolve) => {
                document.addEventListener("click", () => {
                    Tone.start();
                    resolve(true);
                }, { once: true });
            });

            this._audioContext = (Tone.context as any).rawContext;
            Tone.Transport.bpm.value = 120;//Default: 120
            Tone.Transport.start();

            let dataProgress = this._stage.preloader.addProgressList(
                new ProgressList(
                    {
                        label: "Daten"
                    }));

            let audioDownloadProgress = this._stage.preloader.addProgressList(
                new ProgressList(
                    {
                        label: "Audio Download",
                        format: "filesize"
                    }));

            let audioProcessingProgress = this._stage.preloader.addProgressList(
                new ProgressList(
                    {
                        label: "Audio Verarbeitung"
                    }));

            let instrumentDownloadProgress = this._stage.preloader.addProgressList(
                new ProgressList(
                    {
                        label: "Instrumenten Download"
                    }));

            this._data = new Data();

            await this._data.load(this._element.getAttribute("data-data"), dataProgress);

            const instruments: VOMelodyInstrument[] = [];
            for (let item of this._data.config.melody_list.pairs) {
                for (let pair of item.pairs) {
                    if (instruments.indexOf(pair.instrument) === -1) {
                        instruments.push(pair.instrument);
                    }
                }
            }

            await this._data.loadMelodyInstrumentsTones(this._audioContext, instruments, audioDownloadProgress, audioProcessingProgress);
            await this._data.loadMelodies(this._data.data.phil_melody);
            await this._data.loadMelodyVirtualInstruments(instruments, instrumentDownloadProgress);

            this._header = new Header({
                container: this._stage.foreground,
                visible: false,
                collapsed: true
            });

            this._help = new Help({
                container: this._stage.overlay,
                info: this._data.config.melody_info
            });

            this._navigation = new MelodyNavigation({
                container: this._stage.foreground,
                visible: false
            });

            this._confetti = new Confetti({
                container: this._stage.overlay,
                colors: this._data.colors.getAll(),
                labels: this._data.labels
            });

            this._instrumentSelector = new MelodyInstrumentSelector({
                container: this._stage.content,
                labels: this._data.labels,
                items: this._data.config.melody_list.pairs
            });

            this._melodySelector = new MelodySelector({
                container: this._stage.content,
                labels: this._data.labels
            });

            this._instrument = new MelodyInstrument({
                container: this._stage.content,
                labels: this._data.labels,
                confetti: this._confetti,
                debug: this._options.debug,
                showCurly: this._data.config.melody_progress_show_curly,
                showDashes: this._data.config.melody_visual_instrument_dashed
            });;

            this._control = new MelodyControl();
            this._control.ctx = this._instrument.fillContext(this.fillContext({}));
            this._control.state = MelodyControlStates.SELECT_INSTRUMENT;

            this._header.visible = true;

            const idle = new Idle({
                duration: this._data.config.melody_idle_duration * 60 * 1000,
                onIdle: () => {
                    this.setIdle()
                }
            });

        } catch (error) {
            var err = error as Error;
            console.error(error);
            this._stage.preloader.showError(err?.message || "Es ist ein Unbekannter Fehler aufgetreten.");
            return;
        }

        this._stage.preloader.hide();
    }

    private fillContext(ctx: MelodyContext): MelodyContext {
        ctx.instrumentSelector = this._instrumentSelector;
        ctx.nav = this._navigation;
        ctx.help = this._help;
        ctx.header = this._header;
        ctx.melodySelector = this._melodySelector;
        ctx.instrument = this._instrument;
        return ctx;
    }

    private setIdle(): void {
        this._help.visible = false;
        this._control.state = MelodyControlStates.SELECT_INSTRUMENT;
    }
}